import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DocumentForm } from '../documents/form/DocumentForm';
import { DocumentSchema } from '../documents/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { TemplateProvider } from './TemplateProvider';
import { TemplatePreviewPanel } from './TemplatePreviewPanel';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import { useSplitPane } from '../../hooks/useSplitPane';
import {
    getNordInsightAdminTemplate,
    getNordInsightAdminSchema,
    PopulatedPolicy,
} from '../../services/api/policyService';
import { getDepartmentVariableTemplate } from '../../services/api/departmentVariablesService';
import type { VariableOption } from '../documents/form/inputs/mentionSuggestion';
import { toLabel } from '../department-variables/utils/variableUtils';
import { mergePersistedConditionalsIntoDocumentData } from '../department-policies/conditionals/conditionalsUtils';
import { buildPolicyDocumentFunctions } from '../department-policies/services/policyDocumentFunctions';

export default function PolicyTemplateEditor() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const schemaKeyParam = searchParams.get('schema');
    const navigate = useNavigate();
    const { toast } = useToast();

    const [policy, setPolicy] = useState<PopulatedPolicy | null>(null);
    const [schema, setSchema] = useState<DocumentSchema | null>(null);
    const [variableOptions, setVariableOptions] = useState<VariableOption[]>(
        []
    );
    const [loading, setLoading] = useState(true);
    const [title, setTitle] = useState('');
    const [activeEditorTab, setActiveEditorTab] = useState<
        'content' | 'conditionals'
    >('content');
    const {
        splitContainerRef,
        splitPercent,
        showPreview,
        setShowPreview,
        handleDragStart,
    } = useSplitPane();
    const policyFunctions = useMemo(() => buildPolicyDocumentFunctions(), []);

    const titleRef = useRef(title);
    titleRef.current = title;

    useEffect(() => {
        const load = async () => {
            try {
                const [schemaRecord, templateSections] = await Promise.all([
                    getNordInsightAdminSchema(),
                    getDepartmentVariableTemplate(),
                ]);

                setVariableOptions(
                    templateSections.flatMap((section: any) =>
                        section.variables.map((v: any) => ({
                            name: v.name,
                            label: v.label || toLabel(v.name),
                            section: section.title || section.key,
                        }))
                    )
                );

                if (schemaRecord?.schema) {
                    setSchema(schemaRecord.schema as unknown as DocumentSchema);
                }

                if (id) {
                    const p = await getNordInsightAdminTemplate(id);
                    setPolicy(p);
                    setTitle(p.document_id?.title || '');
                }
            } catch {
                toast({
                    title: 'Failed to load template',
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id, schemaKeyParam, toast]);

    const handleCreated = useCallback(
        (newPolicy: PopulatedPolicy) => {
            navigate(`/admin/policy-templates/${newPolicy._id}/edit`, {
                replace: true,
            });
        },
        [navigate]
    );

    const handleCompleted = useCallback(() => {
        navigate('/admin?tab=policy-templates', { replace: true });
    }, [navigate]);

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (!schema) {
        return (
            <div className="container mx-auto max-w-3xl px-4 py-8 text-center">
                <p className="text-muted-foreground">
                    Template or schema not found.
                </p>
                <Button
                    variant="link"
                    onClick={() => navigate('/admin?tab=policy-templates')}
                >
                    Back to templates
                </Button>
            </div>
        );
    }

    const docData = policy?.document_id
        ? {
              data: mergePersistedConditionalsIntoDocumentData(
                  policy.document_id.data,
                  policy.conditionals
              ),
              status: policy.document_id.status,
          }
        : undefined;

    return (
        <div className="min-h-screen flex flex-col bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-4 px-6 py-3 border-b border-subtle/60 bg-background/70 backdrop-blur-sm shrink-0 sticky top-0 z-30">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/admin?tab=policy-templates')}
                    className="shrink-0"
                >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Templates
                </Button>

                <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Template title..."
                    className="max-w-md h-8 text-sm"
                />

                <div className="flex-1" />

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    className="shrink-0"
                >
                    {showPreview ? (
                        <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            Hide preview
                        </>
                    ) : (
                        <>
                            <Eye className="h-4 w-4 mr-1" />
                            Show preview
                        </>
                    )}
                </Button>
            </div>

            {/* Editor + preview */}
            <TemplateProvider
                policyId={policy?._id}
                documentData={docData}
                getTitle={() => titleRef.current}
                schemaKey={schemaKeyParam}
                onCreated={handleCreated}
                onCompleted={handleCompleted}
            >
                <div
                    ref={splitContainerRef}
                    className="flex flex-1 overflow-hidden"
                >
                    <div
                        className="p-6 overflow-y-auto"
                        style={{
                            width: showPreview ? `${splitPercent}%` : '100%',
                        }}
                    >
                        <div className={showPreview ? '' : 'max-w-3xl mx-auto'}>
                            <Tabs
                                value={activeEditorTab}
                                onValueChange={(value) =>
                                    setActiveEditorTab(
                                        value as 'content' | 'conditionals'
                                    )
                                }
                            >
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="content">
                                        Content
                                    </TabsTrigger>
                                    <TabsTrigger value="conditionals">
                                        Conditionals
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent
                                    value="content"
                                    className="mt-3 -mx-6"
                                >
                                    <DocumentForm
                                        schema={schema}
                                        variableOptions={variableOptions}
                                        functions={policyFunctions}
                                        visibleSectionIds={['content', 'actions']}
                                    />
                                </TabsContent>

                                <TabsContent
                                    value="conditionals"
                                    className="mt-3 -mx-6"
                                >
                                    <DocumentForm
                                        schema={schema}
                                        variableOptions={variableOptions}
                                        functions={policyFunctions}
                                        visibleSectionIds={[
                                            'conditionals',
                                            'actions',
                                        ]}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </div>

                    {showPreview && (
                        <>
                            <div
                                onMouseDown={handleDragStart}
                                className="w-1 shrink-0 bg-border hover:bg-primary/40 cursor-col-resize transition-colors"
                            />
                            <div
                                className="bg-muted/30 p-6 overflow-y-auto"
                                style={{ width: `${100 - splitPercent}%` }}
                            >
                                <TemplatePreviewPanel
                                    schema={schema}
                                    title={title}
                                />
                            </div>
                        </>
                    )}
                </div>
            </TemplateProvider>
        </div>
    );
}
